// Facebook Pixel + Conversions API (CAPI) hybrid tracking
// Events are sent to both browser (Pixel) and server (CAPI) for redundancy

const PIXEL_ID = '1598623981276544';

// Generate unique event ID for deduplication between Pixel and CAPI
export function generateEventId() {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Get Facebook cookies for user matching
function getFbCookies() {
  if (typeof document === 'undefined') return { fbp: null, fbc: null };

  const cookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=');
    acc[key] = value;
    return acc;
  }, {});

  return {
    fbp: cookies._fbp || null,
    fbc: cookies._fbc || null,
  };
}

// Get external_id (session ID) for cross-device matching
function getExternalId() {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem('pickify_session_id') || null;
}

// Send event to server-side CAPI endpoint
async function sendToCapiAsync(eventName, eventId, customData = {}, userData = {}) {
  try {
    const apiUrl = process.env.NODE_ENV === 'production'
      ? '/api/meta-capi'
      : '/api/meta-capi';

    const { fbp, fbc } = getFbCookies();
    const externalId = getExternalId();

    const payload = {
      event_name: eventName,
      event_id: eventId,
      event_time: Math.floor(Date.now() / 1000),
      event_source_url: window.location.href,
      user_data: {
        fbp,
        fbc,
        external_id: externalId,
        ...userData,
      },
      custom_data: customData,
      // Uncomment for testing in Meta Events Manager:
      // test_event_code: 'TEST12345',
    };

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      console.warn('📘 CAPI warning:', error);
    } else {
      console.log(`📘 CAPI sent: ${eventName}`);
    }
  } catch (error) {
    // Don't block on CAPI errors - browser pixel is primary
    console.warn('📘 CAPI error (non-blocking):', error.message);
  }
}

// Fire-and-forget CAPI call (non-blocking)
function sendToCapi(eventName, eventId, customData = {}, userData = {}) {
  // Use setTimeout to make it truly non-blocking
  setTimeout(() => {
    sendToCapiAsync(eventName, eventId, customData, userData);
  }, 0);
}

// Browser-side fbq wrapper
function fbq(...args) {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq(...args);
  }
}

// Ensure the pixel is initialised (idempotent – safe to call more than once)
export const initPixel = () => {
  if (typeof window === 'undefined' || !window.fbq) {
    console.log('⚠️ Facebook Pixel not loaded – skipping init');
    return;
  }
  console.log('📘 Facebook Pixel + CAPI ready:', PIXEL_ID);
};

// ============================================
// STANDARD EVENTS (Pixel + CAPI)
// ============================================

export const trackPageView = (eventId = null) => {
  const id = eventId || generateEventId();

  // Browser Pixel with eventID for dedup
  fbq('track', 'PageView', {}, { eventID: id });

  // Server CAPI
  sendToCapi('PageView', id);

  console.log(`📘 PageView tracked (${id})`);
};

export const trackViewContent = (contentName, eventId = null) => {
  const id = eventId || generateEventId();
  const customData = { content_name: contentName };

  fbq('track', 'ViewContent', customData, { eventID: id });
  sendToCapi('ViewContent', id, customData);
};

export const trackInitiateCheckout = (giftTitle, price, currency = 'USD', eventId = null) => {
  const id = eventId || generateEventId();
  const customData = {
    content_name: giftTitle,
    value: price,
    currency: currency,
  };

  fbq('track', 'InitiateCheckout', customData, { eventID: id });
  sendToCapi('InitiateCheckout', id, customData);

  console.log(`📘 InitiateCheckout tracked: ${giftTitle} (${id})`);
};

export const trackLead = (source, userData = {}, eventId = null) => {
  const id = eventId || generateEventId();
  const customData = { content_name: source };

  fbq('track', 'Lead', customData, { eventID: id });
  sendToCapi('Lead', id, customData, userData);

  console.log(`📘 Lead tracked: ${source} (${id})`);
};

export const trackPurchase = (value, currency = 'USD', contentName = '', eventId = null) => {
  const id = eventId || generateEventId();
  const customData = {
    value: value,
    currency: currency,
    content_name: contentName,
  };

  fbq('track', 'Purchase', customData, { eventID: id });
  sendToCapi('Purchase', id, customData);

  console.log(`📘 Purchase tracked: ${value} ${currency} (${id})`);
};

// ============================================
// CUSTOM EVENTS (Pixel + CAPI)
// ============================================

export const trackCustom = (eventName, params = {}, eventId = null) => {
  const id = eventId || generateEventId();

  fbq('trackCustom', eventName, params, { eventID: id });
  sendToCapi(eventName, id, params);
};

// Search event (for quiz completion with specific criteria)
export const trackSearch = (searchTerm, eventId = null) => {
  const id = eventId || generateEventId();
  const customData = { search_string: searchTerm };

  fbq('track', 'Search', customData, { eventID: id });
  sendToCapi('Search', id, customData);
};

// AddToWishlist (for save for later)
export const trackAddToWishlist = (giftTitle, price, eventId = null) => {
  const id = eventId || generateEventId();
  const customData = {
    content_name: giftTitle,
    value: price,
    currency: 'USD',
  };

  fbq('track', 'AddToWishlist', customData, { eventID: id });
  sendToCapi('AddToWishlist', id, customData);
};

// ============================================
// AFFILIATE CLICK TRACKING (Key conversion event)
// ============================================

export const trackAffiliateClick = (giftTitle, price, storeName, position, eventId = null) => {
  const id = eventId || generateEventId();
  const customData = {
    content_name: giftTitle,
    content_category: storeName,
    value: price,
    currency: 'USD',
    content_ids: [position.toString()],
  };

  // Track as InitiateCheckout (standard event) for better attribution
  fbq('track', 'InitiateCheckout', customData, { eventID: id });
  sendToCapi('InitiateCheckout', id, customData);

  // Also track custom event for granular reporting
  const clickData = {
    gift_title: giftTitle,
    gift_price: price,
    store_name: storeName,
    position: position,
  };
  fbq('trackCustom', 'AffiliateClick', clickData, { eventID: `${id}_click` });
  sendToCapi('AffiliateClick', `${id}_click`, clickData);

  console.log(`📘 AffiliateClick tracked: ${storeName} - ${giftTitle} (${id})`);

  return id;
};

// ============================================
// QUIZ FUNNEL EVENTS
// ============================================

export const trackQuizStarted = (eventId = null) => {
  const id = eventId || generateEventId();

  fbq('trackCustom', 'QuizStarted', {}, { eventID: id });
  sendToCapi('QuizStarted', id);

  return id;
};

export const trackQuizCompleted = (answers, eventId = null) => {
  const id = eventId || generateEventId();
  const customData = {
    recipient: answers.recipient,
    occasion: answers.occasion,
    budget: answers.budget,
  };

  fbq('trackCustom', 'QuizCompleted', customData, { eventID: id });
  sendToCapi('QuizCompleted', id, customData);

  // Also track as Lead for funnel optimization
  fbq('track', 'Lead', { content_name: 'quiz_completed' }, { eventID: `${id}_lead` });
  sendToCapi('Lead', `${id}_lead`, { content_name: 'quiz_completed' });

  return id;
};

export const trackResultsViewed = (giftCount, eventId = null) => {
  const id = eventId || generateEventId();
  const customData = { gift_count: giftCount };

  fbq('trackCustom', 'ResultsViewed', customData, { eventID: id });
  sendToCapi('ResultsViewed', id, customData);

  // Track as ViewContent for standard funnel
  fbq('track', 'ViewContent', { content_name: 'gift_results', num_items: giftCount }, { eventID: `${id}_view` });
  sendToCapi('ViewContent', `${id}_view`, { content_name: 'gift_results', num_items: giftCount });

  return id;
};

// ============================================
// EMAIL SUBSCRIPTION (Lead with PII)
// ============================================

export const trackEmailSubscribed = (email, source, eventId = null) => {
  const id = eventId || generateEventId();
  const customData = { content_name: source };

  // Send email hash to CAPI for improved matching
  fbq('track', 'Lead', customData, { eventID: id });
  sendToCapi('Lead', id, customData, { email: email });

  console.log(`📘 Email Lead tracked: ${source} (${id})`);

  return id;
};
